import JSZip from "jszip";
export type DownloadEvidenceItem = {
  assetId: string;
  mediaType: string;
  mimeType: string | null;
  captionDefault: string | null;
  branchKind: string | null;
  itemIndex: number | null;
  detailIndex: number | null;
  slotLabel: string | null;
  groupLabel: string | null;
};
import { fetchProtectedAsset } from "../apiClient";
import { fetchMediaBlobForEvidence } from "./mediaManifestService";
import { fetchMediaTranslation, type MediaTranslation } from "./mediaTranslationService";

type DownloadEntry = {
  name: string;
  blob: Blob;
};

const mimeExtensions: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/mp4": "m4a",
  "video/mp4": "mp4",
  "application/pdf": "pdf",
};

function sanitizeFilename(value: string): string {
  return value.replace(/[<>:"/\\|?*]+/g, "_").trim();
}

function extensionFromMime(mimeType: string | null | undefined): string | null {
  if (!mimeType) {
    return null;
  }
  return mimeExtensions[mimeType] ?? null;
}

function buildBaseName(evidence: DownloadEvidenceItem): string {
  const fallback = evidence.assetId || "evidence";
  const groupLabel = evidence.groupLabel?.trim() || evidence.branchKind?.trim();
  const slotLabel = evidence.slotLabel?.trim();
  const itemSuffix = Number.isFinite(evidence.itemIndex) ? ` ${Number(evidence.itemIndex) + 1}` : "";
  const detailSuffix = Number.isFinite(evidence.detailIndex) ? ` Detail ${Number(evidence.detailIndex) + 1}` : "";

  let label = "";
  if (groupLabel && slotLabel) {
    label = `${groupLabel}${itemSuffix} - ${slotLabel}${detailSuffix}`;
  } else if (slotLabel) {
    label = `${slotLabel}${detailSuffix}`;
  } else if (groupLabel) {
    label = `${groupLabel}${itemSuffix}`;
  } else {
    label = evidence.captionDefault?.trim() || fallback;
  }

  return sanitizeFilename(label || fallback);
}

function blobToDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function htmlToPlainText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const parts: string[] = [];
  const blockTags = new Set(["H1", "H2", "P", "LI"]);

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? "");
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as Element;
    const isBlock = blockTags.has(element.tagName);
    if (isBlock && parts.length > 0) {
      parts.push("\n");
    }

    for (const child of Array.from(element.childNodes)) {
      walk(child);
    }

    if (isBlock) {
      parts.push("\n");
    }
  };

  walk(doc.body);
  return parts.join("")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function buildTranslationEntries(
  translation: MediaTranslation | null,
  baseName: string,
): Promise<DownloadEntry[]> {
  if (!translation || translation.kind === "none") {
    return [];
  }

  if (translation.kind === "html") {
    const text = htmlToPlainText(translation.html);
    return [
      {
        name: `${baseName}-translation.txt`,
        blob: new Blob([text], { type: "text/plain" }),
      },
    ];
  }

  if (translation.kind === "mixed") {
    const text = htmlToPlainText(translation.html);
    const textEntry: DownloadEntry = {
      name: `${baseName}-translation.txt`,
      blob: new Blob([text], { type: "text/plain" }),
    };

    const fileBlob = await fetchProtectedAsset(translation.file.url);
    const fileExtension = extensionFromMime(translation.file.mimeType) ?? "bin";
    const fileName = sanitizeFilename(
      translation.file.filename || `${baseName}-translation.${fileExtension}`,
    );

    return [
      {
        name: fileName,
        blob: fileBlob,
      },
      textEntry,
    ];
  }

  const blob = await fetchProtectedAsset(translation.url);
  const extension = extensionFromMime(translation.mimeType) ?? "bin";
  const filename = sanitizeFilename(translation.filename || `${baseName}-translation.${extension}`);
  return [
    {
      name: filename,
      blob,
    },
  ];
}

async function buildOriginalEntry(evidence: DownloadEvidenceItem, baseName: string): Promise<DownloadEntry> {
  const blob = await fetchMediaBlobForEvidence(evidence);
  const extension = extensionFromMime(evidence.mimeType) ?? evidence.assetId.split(".").pop() ?? "bin";
  const filename = sanitizeFilename(`${baseName}.${extension}`);
  return {
    name: filename,
    blob,
  };
}

async function buildEvidenceEntries(
  evidence: DownloadEvidenceItem,
  translation: MediaTranslation | null,
): Promise<DownloadEntry[]> {
  const baseName = buildBaseName(evidence);
  const entries = [await buildOriginalEntry(evidence, baseName)];
  const translationEntries = await buildTranslationEntries(translation, baseName);
  entries.push(...translationEntries);
  return entries;
}

async function downloadZip(entries: DownloadEntry[], zipName: string): Promise<void> {
  const zip = new JSZip();
  for (const entry of entries) {
    zip.file(entry.name, entry.blob);
  }
  const blob = await zip.generateAsync({ type: "blob" });
  blobToDownload(blob, `${sanitizeFilename(zipName)}.zip`);
}

export async function downloadEvidenceWithTranslation(
  evidence: DownloadEvidenceItem,
  translation: MediaTranslation | null,
): Promise<void> {
  const entries = await buildEvidenceEntries(evidence, translation);
  if (entries.length === 1) {
    blobToDownload(entries[0].blob, entries[0].name);
    return;
  }
  await downloadZip(entries, "evidence-download");
}

export async function downloadEvidenceBundle(evidenceItems: DownloadEvidenceItem[], zipName: string): Promise<void> {
  const entries: DownloadEntry[] = [];
  for (const evidence of evidenceItems) {
    let translation: MediaTranslation | null = null;
    try {
      const translationResponse = await fetchMediaTranslation(evidence.assetId);
      translation = translationResponse.translation ?? null;
    } catch {
      translation = null;
    }

    const nextEntries = await buildEvidenceEntries(evidence, translation);
    entries.push(...nextEntries);
  }
  if (entries.length === 0) {
    return;
  }
  await downloadZip(entries, zipName);
}
